import React from 'react';
import PropTypes from 'prop-types';
import { ltLogger } from '../../bootstrap';
import * as logtypes from '../../common/logtypes';
import { isChunkLoadError } from '../../utils/chunkLoadError';
import CtaButton from './CtaButton';

class ChunkErrorBoundary extends React.Component {
  static getDerivedStateFromError(error) {
    return { error };
  }

  constructor(props) {
    super(props);
    this.state = { error: null, retryKey: 0 };
    this.handleRetry = this.handleRetry.bind(this);
  }

  componentDidCatch(error, errorInfo) {
    ltLogger(logtypes.ERROR, 'lazy_chunk_error', {}, undefined, {
      error,
      metaData: {
        source: this.props.source,
        isChunkLoadError: isChunkLoadError(error),
        componentStack: errorInfo && errorInfo.componentStack,
      },
    });

    if (
      window.ltPhoenix &&
      window.ltPhoenix.callbacks &&
      typeof window.ltPhoenix.callbacks.onCriticalError === 'function'
    ) {
      window.ltPhoenix.callbacks.onCriticalError();
    }
  }

  handleRetry() {
    this.setState((prev) => ({
      error: null,
      retryKey: prev.retryKey + 1,
    }));
  }

  render() {
    const { children, errorFallback } = this.props;
    const { error, retryKey } = this.state;

    if (error) {
      if (typeof errorFallback === 'function') {
        return errorFallback({ error, retry: this.handleRetry });
      }

      return (
        <div
          className="ltChunkBoundaryWrapper"
          role="alert"
          aria-live="assertive"
        >
          <p className="ltChunkBoundary">
            There was a problem displaying this section. Please try refreshing
            the page.
          </p>
          <CtaButton
            text="Refresh Options"
            action={this.handleRetry}
            classExt="ltFormButtonErrorRetry"
          />
        </div>
      );
    }

    return <React.Fragment key={retryKey}>{children}</React.Fragment>;
  }
}

ChunkErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  source: PropTypes.string,
  errorFallback: PropTypes.func,
};

ChunkErrorBoundary.defaultProps = {
  source: 'unknown',
  errorFallback: null,
};

export default ChunkErrorBoundary;
